import assert from 'assert';

import BenchlingConfig from 'BenchlingConfig';
import {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {ExperimentalInputEvaluatedValues} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {MethodExecutionLimitState} from '../execution-status/useMethodExecutionLimitState';

export function areProcedureRunInputsEvaluatedValuesValid(
  evaluatedValues: ExperimentalInputEvaluatedValues
): boolean {
  return Object.values(evaluatedValues).every((methodData) => methodData.blockingValueErrorCount === 0);
}

/* True if conditions are valid for creation, false otherwise. */
export function validateConditions(conditions: Array<BaseCondition>): boolean {
  const conditionsValid = !conditions.some(
    (condition) =>
      condition.validationState?.isNameLengthOverLimit ||
      condition.validationState?.isDuplicateName ||
      condition.validationState?.isDescLengthOverLimit ||
      (condition.numReplicates < 1 && !isPersistedCondition(condition))
  );
  return conditionsValid;
}

function hasDraftConditionWithZeroReplicates(conditions: Array<BaseCondition>): boolean {
  return conditions.some((condition) => condition.numReplicates < 1 && !isPersistedCondition(condition));
}

/**
 * Returns a tooltip string describing why conditions are invalid, or null if they are valid.
 * Used to show the reason why the Save button is disabled.
 */
export function getConditionValidationTooltip(conditions: Array<BaseCondition>): string | null {
  const hasDuplicateNames = conditions.some(({validationState}) => validationState?.isDuplicateName);
  const hasNameTooLong = conditions.some(({validationState}) => validationState?.isNameLengthOverLimit);
  const hasDescTooLong = conditions.some(({validationState}) => validationState?.isDescLengthOverLimit);
  const hasZeroReplicates = hasDraftConditionWithZeroReplicates(conditions);

  const reasons: Array<string> = [];
  if (hasDuplicateNames) {
    reasons.push('duplicate condition names');
  }
  if (hasNameTooLong) {
    reasons.push('condition name is too long');
  }
  if (hasDescTooLong) {
    reasons.push('condition description is too long');
  }
  if (hasZeroReplicates) {
    reasons.push('condition must have at least one replicate');
  }

  if (reasons.length === 0) {
    return null;
  }

  return `Cannot save: ${reasons.join(', ')}`;
}

export function isNumConditionReplicatesValid(methodExecutionLimitState: MethodExecutionLimitState): boolean {
  return !methodExecutionLimitState.isOverLimit;
}

const CONDITION_SETTINGS_CATEGORY = 'Condition settings';

/**
 * Collects all validation errors for a procedure run plan into ErrorBadgeError objects
 * suitable for display in the header error badge.
 *
 * Errors are categorized by method name or "Condition settings" so the badge popover
 * groups them under the appropriate headings.
 */
export function collectPlanErrors({
  evaluatedValues,
  methodExecutionLimitState,
  conditions,
  methodIdToName,
  conditionsText,
  hasNewerVersion,
  procedureString,
}: {
  evaluatedValues: ExperimentalInputEvaluatedValues;
  methodExecutionLimitState: MethodExecutionLimitState;
  conditions: Array<BaseCondition>;
  methodIdToName: Record<string, string>;
  conditionsText: string;
  hasNewerVersion: boolean;
  procedureString: string;
}): Array<ErrorBadgeError> {
  const errors: Array<ErrorBadgeError> = [];

  if (hasNewerVersion) {
    errors.push({
      message: `A new version of this ${procedureString} has been ${BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING ? 'published' : 'created'}`,
      category: 'Version Update Required',
    });
  }

  // Blocking value errors in evaluated values, grouped by method name.
  for (const [methodId, values] of Object.entries(evaluatedValues)) {
    if (values.blockingValueErrorCount > 0) {
      const category = methodIdToName[methodId] ?? '';
      assert(category !== '', `Expected method ${methodId} to have a name`);
      errors.push({
        message: `Some ${conditionsText} have invalid values specified`,
        category,
        count: values.blockingValueErrorCount,
      });
    }
  }

  // Condition-replicate limit exceeded
  if (methodExecutionLimitState.isOverLimit) {
    errors.push({
      message: `Cannot exceed ${methodExecutionLimitState.replicateLimit} total replicates, reduce number of ${conditionsText} or replicates`,
      category: CONDITION_SETTINGS_CATEGORY,
    });
  }

  // Condition name/description validation errors
  for (const condition of conditions) {
    if (condition.numReplicates < 1 && !isPersistedCondition(condition)) {
      errors.push({
        message: `${condition.name} must have at least one replicate`,
        category: CONDITION_SETTINGS_CATEGORY,
      });
    }
    if (condition.validationState?.isDuplicateName) {
      errors.push({message: `Duplicate name: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isNameLengthOverLimit) {
      errors.push({message: `Name is too long: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isDescLengthOverLimit) {
      errors.push({
        message: `Condition description is too long: ${condition.name}`,
        category: CONDITION_SETTINGS_CATEGORY,
      });
    }
  }

  return errors;
}
